import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Store, Book, Users, Package, DollarSign, Award, Megaphone } from 'lucide-react';
import {
  PageContainer,
  MainContent,
  Grid,
  Spacer
} from '../../styles/StyledComponents';
import { DashboardCard } from '../../components/shared/DashboardCard';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { AnnouncementCard } from '../../components/announcements/AnnouncementCard';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';

const DashboardHeading = styled.h1`
  text-align: left;
  margin-bottom: ${Spacing.xl};
  color: ${ThemeColors.secondary.main};
  font-family: 'Roboto', sans-serif;
  font-size: 2rem;
  font-weight: 500;
  padding-left: 50px;
  padding-top: 50px;
`;

const SectionTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.lg};
  margin-top: ${Spacing.xxl};
`;

const QuickActionButton = styled.button`
  background: white;
  border: 2px solid ${ThemeColors.border.light};
  border-radius: 12px;
  padding: ${Spacing.md} ${Spacing.lg};
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${ThemeColors.primary.main};
  font-weight: ${Typography.fontWeight.medium};

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background-color: ${ThemeColors.primary.light};
    transform: translateY(-2px);
  }
`;

export const SalesRepDashboardScreenDesktop = React.memo(() => {
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false); // Expanded by default on desktop
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { 
    announcements, 
    loading: isLoadingAnnouncements 
  } = useAnnouncements({ status: 'ACTIVE' });

  const menuItems = useMemo(() => [
    {
      id: 'store-visit',
      label: t('navigation.storeVisit', 'Store Visit'),
      icon: Store,
      description: t('dashboard.storeVisitDesc', 'Visit stores, manage information, and track customer interactions.'),
      action: () => navigate('/store-visit')
    },
    {
      id: 'resources',
      label: t('navigation.resources', 'Resource Library'),
      icon: Book,
      description: t('dashboard.resourcesDesc', 'Access training materials, product guides, videos, and documents.'),
      action: () => navigate('/resources')
    },
    {
      id: 'store-resource',
      label: t('navigation.storeResource', 'Store Resources'),
      icon: Package,
      description: t('dashboard.storeResourceDesc', 'Access marketing materials, product information, and sales resources.'),
      action: () => navigate('/resources')
    },
    {
      id: 'payroll',
      label: t('navigation.payroll', 'Payroll'),
      icon: DollarSign,
      description: t('dashboard.payrollDesc', 'View commission reports, payment history, and expense tracking.'),
      action: () => navigate('/payroll')
    },
    {
      id: 'contest-entry',
      label: t('navigation.contestEntry', 'Contest Entry'),
      icon: Award,
      description: t('dashboard.contestEntryDesc', 'Participate in sales contests and view performance rankings.'),
      action: () => navigate('/contest-entry')
    }
  ], [t, navigate]);

  return (
    <PageContainer isMenuCollapsed={isMenuCollapsed}>
      <MainContent>
        <DashboardHeading>
          {t('dashboard.welcomeBack', 'Welcome Back')} {user?.firstName || user?.username || ''}
        </DashboardHeading>
          
        {/* Announcements Section - Moved to top after welcome back */}
        {announcements.length > 0 && (
          <>
            <SectionTitle style={{ marginTop: Spacing.lg }}>
              <Megaphone style={{ display: 'inline', marginRight: Spacing.sm, verticalAlign: 'middle' }} />
              Recent Announcements
            </SectionTitle>
            <Grid minWidth="300px" gap={Spacing.md}>
              {announcements.slice(0, 3).map((announcement) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  variant="desktop"
                  onView={(id) => navigate(`/announcements/${id}`)}
                />
              ))}
            </Grid>
            {announcements.length > 3 && (
              <div style={{ textAlign: 'center', marginTop: Spacing.md }}>
                <QuickActionButton onClick={() => navigate('/announcements')}>
                  View All Announcements ({announcements.length})
                </QuickActionButton>
              </div>
            )}
          </>
        )}
          
        <Grid minWidth="250px" gap={Spacing.xl} style={{ marginTop: announcements.length > 0 ? Spacing.xxl : 0 }}>
          {menuItems.map((item) => (
            <DashboardCard
              key={item.id}
              id={item.id}
              label={item.label}
              description={item.description}
              icon={item.icon}
              onClick={item.action}
              variant="desktop"
            />
          ))}
        </Grid>
          
        <Spacer height={Spacing.xxl} />
      </MainContent>
    </PageContainer>
  );
});

SalesRepDashboardScreenDesktop.displayName = 'SalesRepDashboardScreenDesktop';